import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Flags } from 'react-feature-flags'
import { StretchedLegendElement } from 'mbgl-components'
import MapGL, { NavigationControl, Source, Layer, Popup, ScaleControl, FullscreenControl } from '@urbica/react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import PropTypes from 'prop-types'
import turfBbox from '@turf/bbox'
import centroid from '@turf/centroid'
import { LngLatBounds } from 'mapbox-gl'
import GeoCoder from './GeoCoder'
import BoxSelect from './BoxSelect'
import MobilePopup from '../MobilePopup'
import StreetView from './StreetView'
import { deselectParcels, selectParcels as selectParcelsConnect } from '../../actions/parcels'
import { setActiveFeature, setBounds as setBoundsConnect, setZoom as setZoomConnect } from '../../actions/mapSettings'
import ShapeSelect from './ShapeSelect'
import CAMask from '../../../resources/ca-mask.geojson'
import { combinedScreeningOptions, contextualLayers as contextualLayersConfig } from '../../config'
import Legend from '../Legend'
import LegendEl from './LegendEl'
import { generateHatchPattern } from './utils'
import MapboxGLButtonControl from './ButtonControl'
import { initialBounds } from '../../reducers/mapSettings'
import { setDialog as setDialogConnect } from '../../actions/navigation'

const MapComponent = ({
    active,
    basemap,
    contextualLayers,
    mapBounds,
    mode,
    activeFeature,
    zoom,
    selectedParcels,
    setActiveFeature,
    selectParcels,
    setZoom,
    setBounds,
    setDialog,
    unselectParcel,
    filters: rawFilters
}) => {
    const [popupLocation, setPopupLocation] = useState(null)
    const [showLegend, setShowLegend] = useState(true)
    const [showStreetView, setShowStreetView] = useState(false)
    const activeAPN = activeFeature ? activeFeature.properties.parcel_apn || '' : ''
    const unselect = activeAPN !== '' && selectedParcels.includes(activeAPN)
    const mapElementRef = React.useRef(null)
    const [printButton, setPrintButton] = useState(false)

    const [viewport, setViewport] = useState({
        latitude: 37.5,
        longitude: -120,
        zoom
    })

    // Staging Flag workaround
    const params = new URLSearchParams(window.location.search)

    const printMapModal = () => {
        setShowLegend(true)
        setDialog('print')
    }

    useEffect(() => {
        if (!printButton) {
            const map = mapElementRef.current.getMap()
            map.addControl(
                new MapboxGLButtonControl({
                    className: 'map-print-button',
                    title: 'Print Map',
                    eventHandler: printMapModal
                }),
                'top-right'
            )
            setPrintButton(true)
        }
    }, [printButton])

    useEffect(() => {
        // Listen for changes to bounds & zoom to adjust map.
        // If map bounds equal the initial values, uses `fitBounds` to ensure CA is in view.
        if (mapBounds.every((coord, i) => initialBounds[i] === coord)) {
            if (mapElementRef.current) {
                mapElementRef.current.getMap().fitBounds(initialBounds)
            }
        } else {
            const bounds = LngLatBounds.convert(mapBounds)
            const { lng, lat } = bounds.getCenter()
            setViewport({
                latitude: lat,
                longitude: lng,
                zoom
            })
        }
    }, [mapBounds, zoom])

    // Build filter expression from options selected on the "Screen" tab
    let filters = rawFilters
    let filterExpression = []
    Object.values(combinedScreeningOptions).forEach(group => {
        const fields = group.filter(field => filters.includes(field))
        if (fields.length < 1) {
            return
        }

        filterExpression = [...filterExpression, ['any', ...fields.map(field => ['==', field, true])]]

        // Remove any fields used in a combined group
        filters = filters.filter(field => !group.includes(field))
    })
    // A bit hacky, but basically targeting 9_5 (Landslide Hazard) and excluding null values from being filtered out.
    filterExpression = [
        ...filterExpression,
        ...filters.map(field => (field === '9_5' ? ['any', ['!has', field], ['==', field, true]] : ['==', field, true]))
    ]

    const loadFillTextures = map => {
        contextualLayersConfig.forEach(layer => {
            if (layer.style?.hatch) {
                const patternID = `${layer.id}-pattern`
                generateHatchPattern(map, patternID, 64, layer.style.hatch.color, layer.style.hatch.type, 0.1)
                layer.style.paint = { 'fill-pattern': patternID }
            }
        })
    }

    // Given the name of the current basemap, returns the layer ID that our layers should be inserted directly before
    const getInsertionLocation = basemapName => {
        const mapping = {
            Streets: 'building-number-label',
            Light: 'road-label-simple',
            Satellite: ''
        }
        return mapping[basemapName]
    }

    const selectParcel = e => {
        const feature = e.features[0]

        setPopupLocation(e.lngLat)

        if (feature.properties.parcel_id) {
            setActiveFeature(feature)
        } else {
            const features = e.target.queryRenderedFeatures(e.point, { layers: ['selection'] })

            if (features.length) {
                setActiveFeature(features[0])
            }
        }
    }

    const convertBoundsLikeToArray = b => {
        return b.toArray().flat()
    }

    return (
        <>
            <MobilePopup
                unselect={unselect}
                activeAPN={activeAPN}
                activeFeature={activeFeature}
                setActiveFeature={setActiveFeature}
                setPopupLocation={setPopupLocation}
                selectParcels={selectParcels}
                unselectParcel={unselectParcel}
            />
            {/* activeFeature has 'source' if clicked, but not as a search result, so the lat/lng below is to trigger flyTo only on search result, not on parcel click */}
            <MapGL
                style={{ width: '100%', height: '100%' }}
                mapStyle={basemap.url}
                accessToken={process.env.MAPBOX_TOKEN}
                latitude={
                    activeFeature && activeFeature?.source === undefined
                        ? centroid(activeFeature).geometry.coordinates[1]
                        : viewport.latitude
                }
                longitude={
                    activeFeature && activeFeature?.source === undefined
                        ? centroid(activeFeature).geometry.coordinates[0]
                        : viewport.longitude
                }
                zoom={viewport.zoom}
                maxZoom={17}
                onViewportChange={v => {
                    const bounds = convertBoundsLikeToArray(mapElementRef.current.getMap().getBounds())
                    setZoom(v.zoom)
                    setBounds(bounds)
                }}
                viewportChangeMethod="flyTo"
                viewportChangeOptions={{ duration: 3000 }}
                dragRotate={false}
                ref={mapElementRef}
                preserveDrawingBuffer
                onLoad={() => {
                    // load fill textures on map load or style update
                    const map = mapElementRef.current.getMap()
                    loadFillTextures(map)
                    map.on('style.load', () => {
                        loadFillTextures(map)
                    })
                    // This resets the viewport to prevent hatch/crosshatch patterns from defaulting to black on load of sharelinks.
                    setViewport({ ...viewport })
                }}
            >
                <ScaleControl unit="imperial" position="top-left" />
                <GeoCoder position="top-right" accessToken={process.env.MAPBOX_TOKEN} />
                <NavigationControl showCompass={false} showZoom position="top-right" />
                <FullscreenControl position="top-right" />

                <Source id="caMask" type="geojson" data={CAMask} />
                <Layer
                    id="caMask"
                    type="fill"
                    source="caMask"
                    paint={{
                        'fill-color': '#fff',
                        'fill-opacity': 0.8
                    }}
                />
                {/* LEGEND */}
                {contextualLayers.filter(lyr => lyr.visible && lyr.legends).length > 0 && (
                    <Legend active={showLegend}>
                        {contextualLayers
                            .filter(lyr => lyr.visible && lyr.legends)
                            .map(lyr => (
                                <div className="legend-layer" key={lyr.id}>
                                    {lyr.renderType === 'stretched' ? (
                                        <StretchedLegendElement
                                            colors={[lyr.legends[0].color, lyr.legends[1].color]}
                                            labels={[lyr.legends[0].label, lyr.legends[1].label]}
                                        />
                                    ) : (
                                        lyr.legends.map(legend => {
                                            // hackey workaround for parcels layer's variable coloring
                                            let lineColor = legend.lineColor || legend.color
                                            if (lyr.id === 'parcels' && basemap.name === 'Satellite') {
                                                lineColor = '#7476D2'
                                            }
                                            return (
                                                <React.Fragment key={legend.label}>
                                                    <LegendEl
                                                        symbol={legend.type}
                                                        color={legend.color || 'transparent'}
                                                        lineColor={lineColor}
                                                        labels={legend.label}
                                                    />
                                                </React.Fragment>
                                            )
                                        })
                                    )}
                                </div>
                            ))}
                    </Legend>
                )}

                {/* POPUP */}
                {activeFeature !== null && mode !== 'shape' ? (
                    <Popup
                        latitude={activeFeature ? centroid(activeFeature).geometry.coordinates[1] : popupLocation?.lat}
                        longitude={activeFeature ? centroid(activeFeature).geometry.coordinates[0] : popupLocation?.lng}
                        closeButton={false}
                        closeOnClick={false}
                        maxWidth="350px"
                    >
                        <div className="parcel-popup">
                            {showStreetView && <StreetView activeFeature={activeFeature} />}
                            <table>
                                <tbody>
                                    <tr>
                                        <td>APN</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.parcel_apn || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>CITY</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.site_city || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>COUNTY</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.countyname || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>MPO</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.mpo || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>SPECIFIC PLAN</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.specific_plan || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>ADDRESS</td>
                                        <td className="has-text-weight-bold">
                                            <span>{activeFeature.properties.site_addr || 'No street address'}</span>
                                            <br />
                                            <span>
                                                {activeFeature.properties.site_city}&nbsp;
                                                {activeFeature.properties.site_state}&nbsp;
                                                {activeFeature.properties.site_zip}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>CENSUS BLOCK</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.census_block.split(' ').pop() || 'No data'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>ZONING DESIGNATION</td>
                                        <td className="has-text-weight-bold">
                                            {activeFeature.properties.zoning_designation
                                                ? Object.keys(
                                                      JSON.parse(activeFeature.properties.zoning_designation)
                                                  ).join(', ')
                                                : 'No data'}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td colSpan="2">
                                            <a onClick={() => setShowStreetView(!showStreetView)}>
                                                {showStreetView ? 'hide' : 'show'} street view
                                            </a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="popup-buttons">
                            <div>
                                <button
                                    type="button"
                                    className="button is-neutral"
                                    onClick={() => {
                                        setActiveFeature(null)
                                        setPopupLocation(null)
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="button is-primary"
                                    onClick={() => {
                                        if (unselect) {
                                            unselectParcel(activeAPN)
                                        } else {
                                            const { properties } = activeFeature

                                            selectParcels({
                                                id: properties.parcel_id,
                                                apn: activeAPN,
                                                county: properties.countyname.toLowerCase(),
                                                address: {
                                                    address: properties.site_addr,
                                                    city: properties.site_city,
                                                    zip: properties.site_zip,
                                                    state: 'CA',
                                                    label: `${properties.site_addr}, ${properties.site_city} ${properties.site_zip}`
                                                },
                                                // Will likely be useless until Urbica properly receives bounds, contrary to their docs
                                                bounds: turfBbox(activeFeature),
                                                mpo: properties.mpo,
                                                specific_plan: properties.specific_plan,
                                                census_block: properties.census_block.split(' ').pop(),
                                                zoning_designation: JSON.parse(properties.zoning_designation)
                                            })
                                        }

                                        setActiveFeature(null)
                                        setPopupLocation(null)
                                        // activeFeature has 'source' if clicked, but not as a search result, so the lat/lng below is to trigger flyTo only on search result, not on parcel click
                                        // This also returns user to last coordinates via viewport, if the don't select the parcel.
                                        if (activeFeature && activeFeature?.source === undefined) {
                                            setViewport({
                                                ...viewport,
                                                longitude: turfBbox(activeFeature)[0],
                                                latitude: turfBbox(activeFeature)[1]
                                            })
                                        } else {
                                            setViewport(viewport)
                                        }
                                    }}
                                >
                                    {unselect ? 'Unselect' : 'Select'}
                                </button>
                            </div>
                        </div>
                    </Popup>
                ) : null}

                {/* ALL SOURCES */}
                {contextualLayersConfig.map(lyr => {
                    let source
                    if (lyr.tileType !== 'raster') {
                        source = (
                            <Source
                                id={lyr.id}
                                type="vector"
                                url={SETTINGS.tileserverRoot + lyr.tileName}
                                attribution={lyr.attribution ?? ''}
                            />
                        )
                    } else {
                        // special configuration for raster tile sources
                        source = (
                            <Source
                                id={lyr.id}
                                type="raster"
                                tileSize={256}
                                tiles={
                                    lyr.subdomains.length > 0
                                        ? lyr.subdomains.map(subdomain => lyr.url.replace('{s}', subdomain))
                                        : [`${SETTINGS.tileserverRoot}${lyr.tileName}/tiles/{z}/{x}/{y}.png`]
                                }
                                attribution={lyr.attribution ?? ''}
                            />
                        )
                    }

                    return <React.Fragment key={`${lyr.id}-source`}>{source}</React.Fragment>
                })}

                {/* MOST LAYERS */}
                {contextualLayers
                    .filter(lyr => lyr.visible && lyr.id !== 'parcels')
                    .map(lyr => {
                        // map generic 'opacity' property onto the relevant paint properties for each layer
                        const opacityTerms = {
                            fill: ['fill-opacity'],
                            line: ['line-opacity'],
                            circle: ['circle-opacity', 'circle-stroke-opacity'],
                            raster: ['raster-opacity'],
                            heatmap: []
                        }
                        const opacity = {}
                        opacityTerms[lyr.tileType].forEach(term => {
                            opacity[term] = lyr.opacity ?? 1
                        })

                        // heatmap layer has special source-layer value
                        const sourceLayer = lyr.id === 'heatmap' ? 'data' : lyr.tileName

                        // optional outline layer
                        let outlineLayer
                        if (lyr.style.outlinePaint) {
                            outlineLayer = (
                                <>
                                    <Layer
                                        id={`${lyr.id}-outline`}
                                        before={getInsertionLocation(basemap.name)}
                                        type="line"
                                        source={lyr.id}
                                        source-layer={sourceLayer}
                                        paint={{
                                            'line-opacity': lyr.opacity ?? 1,
                                            ...lyr.style.outlinePaint
                                        }}
                                    />
                                </>
                            )
                        }

                        return (
                            <React.Fragment key={`${lyr.id}-layer`}>
                                <Layer
                                    id={lyr.id}
                                    before={getInsertionLocation(basemap.name)}
                                    type={lyr.tileType}
                                    source={lyr.id}
                                    source-layer={sourceLayer}
                                    paint={{
                                        ...opacity,
                                        ...lyr.style.paint
                                    }}
                                />
                                {outlineLayer}
                            </React.Fragment>
                        )
                    })}

                {/* PARCELS LAYER */}
                {(contextualLayers.find(lyr => lyr.id === 'parcels') || active !== 'explore') && (
                    <>
                        <Layer
                            id="unselectedParcels"
                            before={getInsertionLocation(basemap.name)}
                            interactive={active === 'analyze' && mode === 'single'}
                            type="fill"
                            source="parcels"
                            source-layer="data"
                            paint={{
                                'fill-color': 'transparent'
                            }}
                            onClick={mode !== 'shape' ? selectParcel : null}
                            onTouchEnd={mode !== 'shape' ? selectParcel : null}
                            filter={['all', ['!in', 'parcel_apn', ...selectedParcels], ...filterExpression]}
                        />
                        <Layer
                            id="unselectedParcelLine"
                            before={getInsertionLocation(basemap.name)}
                            type="line"
                            source="parcels"
                            source-layer="data"
                            paint={{
                                'line-color': [
                                    'case',
                                    ['==', ['get', 'parcel_apn'], activeAPN],
                                    '#4783D7',
                                    basemap.name === 'Satellite' ? '#7476D2' : '#888888'
                                ],
                                'line-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 10, 0.75],
                                'line-width': ['case', ['==', ['get', 'parcel_apn'], activeAPN], 5, 2],
                                'line-blur': ['case', ['==', ['get', 'parcel_apn'], activeAPN], 3, 0]
                            }}
                            filter={['all', ['!in', 'parcel_apn', ...selectedParcels], ...filterExpression]}
                        />
                        {/* Selected parcel style */}
                        <Layer
                            id="selectedParcels"
                            before={getInsertionLocation(basemap.name)}
                            type="fill"
                            source="parcels"
                            source-layer="data"
                            interactive={active === 'analyze' && mode === 'single'}
                            paint={{
                                'fill-color': '#54B6B4',
                                'fill-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 10, 0.85]
                            }}
                            filter={['all', ['in', 'parcel_apn', ...selectedParcels], ...filterExpression]}
                            onClick={mode !== 'shape' ? selectParcel : null}
                            onTouchEnd={mode !== 'shape' ? selectParcel : null}
                        />
                        <Layer
                            id="selectedParcelLine"
                            before={getInsertionLocation(basemap.name)}
                            type="line"
                            source="parcels"
                            source-layer="data"
                            paint={{
                                'line-color': '#174F4E',
                                'line-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 10, 0.85],
                                'line-width': 3,
                                'line-blur': ['case', ['==', ['get', 'parcel_apn'], activeAPN], 2, 0]
                            }}
                            filter={['all', ['in', 'parcel_apn', ...selectedParcels], ...filterExpression]}
                        />
                        <Layer
                            id="selection"
                            before={getInsertionLocation(basemap.name)}
                            type="fill"
                            source="parcels"
                            source-layer="selection"
                            interactive={false}
                            paint={{
                                'fill-color': [
                                    'case',
                                    ['==', ['get', 'parcel_apn'], activeAPN],
                                    '#BDD6FA',
                                    ['in', ['get', 'parcel_apn'], ['literal', selectedParcels]],
                                    '#54B6B4',
                                    '#FF0000'
                                ],
                                'fill-opacity': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    7,
                                    0,
                                    10,
                                    [
                                        'case',
                                        [
                                            'any',
                                            ['==', ['get', 'parcel_apn'], activeAPN],
                                            ['in', ['get', 'parcel_apn'], ['literal', selectedParcels]]
                                        ],
                                        0.75,
                                        0
                                    ]
                                ]
                            }}
                        />
                    </>
                )}

                {/* Parcel Select Box */}
                {mode === 'box' && active === 'analyze' && (
                    <BoxSelect
                        selectParcels={selectParcels}
                        deselectParcels={unselectParcel}
                        map={mapElementRef.current.getMap()}
                    />
                )}
                {mode === 'shape' && active === 'analyze' && (
                    <ShapeSelect
                        selectParcels={selectParcels}
                        deselectParcels={unselectParcel}
                        map={mapElementRef.current.getMap()}
                    />
                )}
            </MapGL>
        </>
    )
}

MapComponent.propTypes = {
    active: PropTypes.string.isRequired,
    mode: PropTypes.string.isRequired,
    // redux state:
    basemap: PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        attribution: PropTypes.string,
        maxZoom: PropTypes.number,
        subdomains: PropTypes.array
    }).isRequired,
    contextualLayers: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedParcels: PropTypes.arrayOf(PropTypes.string),
    mapBounds: PropTypes.arrayOf(PropTypes.number),
    zoom: PropTypes.number.isRequired,
    // redux actions:
    setZoom: PropTypes.func,
    setBounds: PropTypes.func,
    selectParcels: PropTypes.func.isRequired,
    unselectParcel: PropTypes.func.isRequired
}

MapComponent.defaultProps = {
    setZoom: zoom => zoom,
    setBounds: bounds => bounds,
    selectedParcels: []
}

export default connect(
    ({
        parcels: { items },
        layers: { contextualLayers, basemap },
        mapSettings: { bounds: mapBounds, mode, activeFeature, zoom },
        screen: filters
    }) => ({
        basemap,
        contextualLayers: contextualLayers.map(lyr => ({
            ...contextualLayersConfig.find(ctx => ctx.id === lyr.id),
            ...lyr,
            ...{ visible: true }
        })),
        mapBounds,
        selectedParcels: items.filter(item => item.type === 'parcel').map(item => item.apn),
        mode,
        activeFeature,
        zoom,
        filters
    }),
    dispatch => ({
        setActiveFeature: feature => dispatch(setActiveFeature(feature)),
        setZoom: zoom => dispatch(setZoomConnect(zoom)),
        setBounds: bounds => dispatch(setBoundsConnect(bounds)),
        selectParcels: (apns, bounds) => dispatch(selectParcelsConnect(apns, bounds)),
        unselectParcel: apn => dispatch(deselectParcels(apn)),
        setDialog: (dialog, modalProps) => dispatch(setDialogConnect(dialog, modalProps))
    })
)(MapComponent)
